import { Check } from "lucide-react"
import { cn } from "@/lib/utils"
import { Spinner } from "@/components/ui/spinner"
import type { ChatProgressFrame } from "@/types/chat.types"

interface ChatProgressIndicatorProps {
  frames: ChatProgressFrame[]
  variant?: "panel" | "page"
}

export function ChatProgressIndicator({ frames, variant = "panel" }: ChatProgressIndicatorProps) {
  const items = buildDisplayItems(frames)

  return (
    <div className="flex gap-3 justify-start">
      <div className={cn(
        "mt-0.5 flex shrink-0 items-center justify-center rounded-md bg-muted",
        variant === "page" ? "size-8 rounded-lg" : "size-7",
      )}>
        <img
          src="/img/logo.png"
          alt="AI"
          className="size-4 object-contain opacity-70"
        />
      </div>
      <div className="flex flex-col gap-1.5 py-1">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
            {item.status === "active" && (
              <Spinner className="size-3.5 text-muted-foreground" />
            )}
            {item.status === "done" && (
              <Check className="size-3.5 text-emerald-500" />
            )}
            <span className={cn(
              item.status === "done" && "text-muted-foreground/70",
            )}>
              {item.label}
            </span>
            {item.durationMs != null && (
              <span className="text-xs text-muted-foreground/50">
                {(item.durationMs / 1000).toFixed(1)}s
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

interface DisplayItem {
  label: string
  status: "active" | "done"
  durationMs?: number
}

function buildDisplayItems(frames: ChatProgressFrame[]): DisplayItem[] {
  const items: DisplayItem[] = []
  const toolState = new Map<string, DisplayItem>()

  for (const frame of frames) {
    if (frame.kind === "typing" && frame.thinkingState) {
      const existing = items.find((it) => it.label === frame.thinkingState!.label)
      if (!existing) {
        items.push({ label: frame.thinkingState.label, status: "active" })
      }
    } else if (frame.kind === "tool_start" && frame.toolProgress) {
      const item: DisplayItem = { label: frame.toolProgress.toolLabel, status: "active" }
      toolState.set(frame.toolProgress.toolName, item)
      items.push(item)
    } else if (frame.kind === "tool_done" && frame.toolProgress) {
      const existing = toolState.get(frame.toolProgress.toolName)
      if (existing) {
        existing.label = frame.toolProgress.toolLabel
        existing.status = "done"
        existing.durationMs = frame.toolProgress.durationMs
      } else {
        items.push({
          label: frame.toolProgress.toolLabel,
          status: "done",
          durationMs: frame.toolProgress.durationMs,
        })
      }
    }
  }

  // Once any tool starts, remove the thinking indicator
  if (items.some((it) => toolState.has(it.label) || it.status === "done")) {
    const thinkingIdx = items.findIndex((it) => !toolState.has(it.label) && it.status === "active" && !it.durationMs)
    if (thinkingIdx === 0) items.splice(thinkingIdx, 1)
  }

  return items
}
